import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocalizationContext, useMbm } from '@amzn/react-arb-tools';
import type { MessageBundle } from '@amzn/arb-tools';

// Reported once each: unusable messages (keyed bundle/id) and failed packs (keyed bundle).
const reportedMissing = new Set<string>();
const reportedBundles = new Set<string>();

/**
 * Whether the read failed only because this bundle does not carry the id. That is the ordinary case
 * for a reader asking several bundles for one string, and it is the one failure worth staying quiet
 * about: every other error means the message is there but cannot be used — a tag the caller supplied
 * no component for, an argument the message needs, markup a translator left unpaired — and those show
 * the English source in one locale while looking healthy in the rest.
 */
function isAbsentId(error: unknown): boolean {
  return error instanceof Error && error.message.startsWith('Resource does not exist');
}

/** Test-only: clears the dedupe sets so each test observes reporting from a clean slate. */
export function __resetReportedMissing(): void {
  reportedMissing.clear();
  reportedBundles.clear();
}

export type Messages = {
  /**
   * Reports an id no pack carries: this reader is the only one asked, so blank text here means the
   * string is simply gone, and nothing else would surface that.
   */
  message: (id: string) => string;
  /**
   * Blank, and silent, for an id this bundle does not carry. Silent because the caller may be asking
   * several bundles in turn for one string, and a bundle that does not hold it is the ordinary case
   * rather than a fault — useLocalizedText falls back to the English i18next string either way. A
   * message the bundle does carry but cannot format is still reported.
   */
  format: (id: string, args?: Record<string, unknown>) => string;
  /**
   * A message whose paired inline tags (`<b>…</b>`) have been replaced by the components the caller
   * supplied as a function per tag in `args`. Separate from format() because such a message does not
   * format to a string: the bundle types formatMessage as returning one, but a tag argument that
   * returns a React element makes it return the formatted parts instead. Silent, as format() is.
   */
  formatRich: (id: string, args?: Record<string, unknown>) => ReactNode;
  isLoading: boolean;
};

/**
 * Loads one or more RedVelvetTranslations bundles for the locale in effect and returns a reader per
 * bundle name.
 *
 * Reads through the ARB MessageBundleManager (useMbm) directly rather than the single-bundle
 * useBundle hook, so a component can pull from several bundles without a hook-per-bundle — which the
 * Rules of Hooks forbid. Mirrors what useBundle does internally (useMbm → effect → getBundle →
 * state), extended to N bundles and guarded when no MbmProvider is mounted.
 *
 * Text is blank until the pack arrives, and blank for any string it turns out not to carry; a failed
 * pack is reported once, and so is an id message() could not find, since blank text is otherwise
 * invisible to us. format() and formatRich() stay silent about an absent id: their caller asks
 * several bundles for one string and shows the English source when none of them has it, so a bundle
 * without the id is the ordinary case rather than something to report.
 */
export function useBundles(bundleNames: string[]): Record<string, Messages> {
  const mbm = useMbm();
  const { localizationContext } = useLocalizationContext();
  const [bundles, setBundles] = useState<Record<string, MessageBundle | undefined>>({});
  const hasProvider = typeof mbm?.getBundle === 'function';
  // Start "loading" only when a provider is present; without one (a bare test) there is nothing to
  // load, so we avoid an extra state update / re-render.
  const [isLoading, setIsLoading] = useState(() => hasProvider);
  // bundleNames is a fresh array each render; this string is its stable identity for the deps below.
  const namesKey = bundleNames.join('\u0000');

  useEffect(() => {
    let alive = true;
    if (typeof mbm?.getBundle !== 'function') {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    Promise.all(
      bundleNames.map((name) =>
        Promise.resolve(mbm.getBundle(name, localizationContext)).catch(() => undefined)
      )
    ).then((loaded) => {
      if (!alive) {
        return;
      }
      setBundles(Object.fromEntries(bundleNames.map((name, i) => [name, loaded[i]])));
      setIsLoading(false);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- namesKey stands in for bundleNames; localizationContext reloads packs on a locale switch
  }, [mbm, localizationContext, namesKey]);

  return useMemo(() => {
    const make = (name: string): Messages => {
      const bundle = bundles[name];
      const resolve = <T>(
        blank: T,
        read: (b: MessageBundle) => T,
        onMissing?: (error: unknown) => void
      ): T => {
        if (isLoading) {
          return blank;
        }
        if (!bundle) {
          // Silent where no MbmProvider is mounted at all: that is a tree outside localization (the
          // pre-authentication screens, a bare unit test), not a pack that failed to arrive.
          if (hasProvider && !reportedBundles.has(name)) {
            reportedBundles.add(name);
            console.error(`Showing no text for ${name}: its language pack could not be loaded`);
          }
          return blank;
        }
        try {
          return read(bundle);
        } catch (error) {
          onMissing?.(error);
          return blank;
        }
      };
      const reportMissing = (id: string) => (error: unknown) => {
        const key = `${name}/${id}`;
        if (!reportedMissing.has(key)) {
          reportedMissing.add(key);
          console.error(`Showing no text for ${key}: ${error}`);
        }
      };
      // A message this bundle carries but cannot format is reported whichever reader asked for it: it
      // renders the English source, so the only other symptom is a sentence that never translates.
      const reportUnusable = (id: string) => (error: unknown) => {
        if (!isAbsentId(error)) {
          reportMissing(id)(error);
        }
      };
      return {
        message: (id) => resolve('', (b) => b.getMessage(id), reportMissing(id)),
        format: (id, args) =>
          resolve('', (b) => b.formatMessage(id, args ?? {}), reportUnusable(id)),
        formatRich: (id, args) =>
          resolve<ReactNode>('', (b) => b.formatMessage(id, args ?? {}), reportUnusable(id)),
        isLoading,
      };
    };
    return Object.fromEntries(bundleNames.map((name) => [name, make(name)]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- namesKey stands in for bundleNames
  }, [bundles, hasProvider, isLoading, namesKey]);
}

/** Single-bundle convenience — language picker, warm-up, and existing callers use this unchanged. */
export function useMessages(bundleName: string): Messages {
  return useBundles([bundleName])[bundleName];
}
